import * as XLSX from "xlsx";
import {
	AsrsUploadSingleColumnRequired,
	ValidateAsrsUploadColumnCount,
} from "lib/asrs/uploadLayout";
import { ValidateFlexNetUploadColumnCount } from "lib/flexnet/uploadTemplate";
import * as util from "lib/util";
import {
	DecodeCsv,
	IsBlankCsvRecord,
	IsCsvFileName,
	FirstUsedCsvColumn,
	LastUsedCsvColumn,
	IterateCsv,
} from "./csv";

const maxAllowedCols = 2;

export const InvalidUploadFileExtension =
	"Invalid file extension. Only Excel (.xlsx) and CSV (.csv) files are supported.";

export type ProcessFileOptions = {
	/**
	 * FlexNet PN gate layout. `true` = 2 cols (PN + Serial); `false` = 1 col
	 * (Serial only). `null`/`undefined` skips the FlexNet layout check.
	 */
	partNumberRequired?: boolean | null;
	/** When true, enforce ASRS single-column layout (Serial Number or LPN). */
	asrsUpload?: boolean;
	/** ASRS copy: LPN vs Serial Number. Ignored unless `asrsUpload` is true. */
	byLpn?: boolean;
};

/** First-sheet rows keyed by header, plus the used range (e.g. "A1:B11"). */
type sheetData = {
	rawJsonData: Array<Record<string, unknown>>;
	ref: string | undefined;
};

export function IsJsonValid(json: any): json is Record<string, string> {
	if (!json) return false;
	return !!Object.keys(json).length;
}

function readXlsxSheet(data: Uint8Array, rowLimit: number): sheetData | string {
	const readOptions: XLSX.ParsingOptions = {
		type: "array",
		sheetRows: rowLimit + 1,
	};

	const workbook = XLSX.read(data, readOptions);

	if (!workbook || workbook.SheetNames.length === 0)
		return "Invalid Excel file format or empty workbook";

	const sheetName = workbook.SheetNames[0];
	const worksheet = workbook.Sheets[sheetName];

	const numRows: number = workbook["Strings"]?.length || 0;

	if (!worksheet) return "Empty worksheet";

	const rawJsonData: Array<Record<string, unknown>> =
		XLSX.utils.sheet_to_json(worksheet, {
			blankrows: true,
			defval: "",
		});

	if (!Array.isArray(rawJsonData) || rawJsonData.length === 0)
		return "No data found in the Excel file";

	if (numRows >= 11 && rawJsonData.length < 10) {
		const rowsToAppend = 10 - rawJsonData.length;
		const headers = Object.keys(rawJsonData[0]).filter(
			(k) => k !== "__rowNum__"
		);
		const emptyRow: Record<string, unknown> = {};
		for (const h of headers) {
			emptyRow[h] = "";
		}

		for (let i = 0; i < rowsToAppend; i++) {
			rawJsonData.Append({ ...emptyRow });
		}
	}

	return { rawJsonData, ref: worksheet["!ref"] };
}

/** Header keys the way xlsx's sheet_to_json names them. */
function csvHeaderKeys(header: string[], width: number): string[] {
	const seen = new Map<string, number>();
	const keys: string[] = [];
	for (let c = 0; c < width; c++) {
		const base = header[c] ? header[c] : "__EMPTY";
		const count = seen.get(base) ?? 0;
		seen.set(base, count + 1);
		keys.push(count === 0 ? base : `${base}_${count}`);
	}
	return keys;
}

function columnLetter(col: number): string {
	let letters = "";
	for (let n = col; n > 0; n = Math.floor((n - 1) / 26)) {
		letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
	}
	return letters;
}

/**
 * Reads a CSV with the same shape as {@link readXlsxSheet}. Like the backend
 * and Excel, the header is the first non-blank row, blank rows between data
 * rows are kept for the preview and trailing blank rows are ignored.
 */
function readCsvSheet(data: Uint8Array, rowLimit: number): sheetData | string {
	// Read only what the preview needs: leading blank rows, the header and up
	// to rowLimit rows. Like Excel's sheetRows, later rows are left to the
	// backend. Lookahead past the window happens only when the window ends in
	// blank rows, to tell interior blank rows from trailing ones.
	let headerIdx = -1;
	let header: string[] = [];
	const previewRows: string[][] = [];
	let seenDataAfterWindow = false;
	let idx = 0;
	const records = IterateCsv(DecodeCsv(data));
	for (let next = records.next(); !next.done; next = records.next()) {
		const record = next.value;
		if (headerIdx < 0) {
			if (!IsBlankCsvRecord(record)) {
				headerIdx = idx;
				header = record;
			}
		} else {
			previewRows.push(record);
			if (previewRows.length === rowLimit) break;
		}
		idx++;
	}

	if (
		previewRows.length === rowLimit &&
		IsBlankCsvRecord(previewRows[rowLimit - 1])
	) {
		try {
			for (let next = records.next(); !next.done; next = records.next()) {
				if (!IsBlankCsvRecord(next.value)) {
					seenDataAfterWindow = true;
					break;
				}
			}
		} catch {
			// Content exists past the window; the backend reports the bad row.
			seenDataAfterWindow = true;
		}
	}

	let bodyLength = previewRows.length;
	if (!seenDataAfterWindow) {
		while (
			bodyLength > 0 &&
			IsBlankCsvRecord(previewRows[bodyLength - 1])
		) {
			bodyLength--;
		}
	}
	if (headerIdx < 0 || bodyLength === 0)
		return "No data found in the CSV file";

	const body = previewRows.slice(0, bodyLength);

	const width = Math.max(header.length, ...body.map((r) => r.length));
	const keys = csvHeaderKeys(header, width);
	const rawJsonData = body.map((record) =>
		keys.reduce<Record<string, unknown>>((row, key, c) => {
			row[key] = record[c] ?? "";
			return row;
		}, {})
	);

	const usedRows = [header, ...body];
	const firstUsedCol = FirstUsedCsvColumn(usedRows);
	const lastUsedCol = LastUsedCsvColumn(usedRows);
	const ref =
		firstUsedCol && lastUsedCol
			? `${columnLetter(firstUsedCol)}${headerIdx + 1}:${columnLetter(lastUsedCol)}${headerIdx + 1 + body.length}`
			: undefined;

	return { rawJsonData, ref };
}

export function ProcessFile(
	file: File,
	rowLimit = 10,
	options?: ProcessFileOptions
): Promise<Record<string, string>[]> {
	return new Promise((resolve, reject) => {
		// Validate file extension; the extension (not MIME) picks the parser
		const isCsv = IsCsvFileName(file.name);
		if (!isCsv && !file.name.toLowerCase().endsWith(".xlsx")) {
			return reject(InvalidUploadFileExtension);
		}

		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				let data: Uint8Array | null = null;
				if (e.target && e.target.result) {
					data = new Uint8Array(e.target.result as ArrayBuffer);
				}

				if (!data) return reject("Error reading file data");

				const sheet = isCsv
					? readCsvSheet(data, rowLimit)
					: readXlsxSheet(data, rowLimit);
				if (typeof sheet === "string") return reject(sheet);

				const { rawJsonData, ref } = sheet;

				const jsonData = rawJsonData.Map((o: any) => {
					if (util.IsPlainObject(o)) {
						return Object.entries(o).Fold(
							{} as Record<string, string>,
							(acc, [k, v]) => {
								// Drop xlsx's __rowNum__ metadata key — otherwise both the
								// column-count check and the empty-row check below would be
								// wrong (it counts as an extra column and always stringifies
								// to a non-empty value).
								if (k === "__rowNum__") return acc;
								if (k.ToLower().Contains("empty")) return acc;
								acc[k] =
									v !== null && v !== undefined
										? String(v).TrimSpace()
										: "";
								return acc;
							}
						);
					}
					throw new Error("Invalid row format");
				});

				if (!util.IsJsonValid(jsonData[0])) {
					return reject(
						isCsv
							? "Invalid file format. Please upload a valid CSV file."
							: "Invalid file format. Please upload a valid Excel file."
					);
				}

				const colCount = Object.keys(jsonData[0]).length;
				const asrsLayoutError = ValidateAsrsUploadColumnCount(
					colCount,
					options?.asrsUpload === true,
					options?.byLpn ?? false
				);
				if (asrsLayoutError) {
					return reject(asrsLayoutError);
				}

				const gateLayoutError = ValidateFlexNetUploadColumnCount(
					colCount,
					options?.partNumberRequired
				);
				if (gateLayoutError) {
					return reject(gateLayoutError);
				}

				if (colCount < 1 || colCount > maxAllowedCols) {
					return reject(
						`Invalid file format. File must have 1 or 2 columns, but found ${colCount}.`
					);
				}

				if (
					jsonData.All((row) =>
						Object.values(row).All(
							(value) => isNil(value) || value === ""
						)
					)
				) {
					return reject(
						`Invalid file format. ${rowLimit} rows after the header contain empty rows.`
					);
				}

				if (ref) {
					const [start, end] = ref.Split(":");
					if (!start || !end)
						return reject("Invalid sheet reference");

					const startCol = start.Replace(/[0-9]/g, "");
					const endCol = end.Replace(/[0-9]/g, "");

					if (options?.asrsUpload) {
						if (startCol !== "A" || endCol !== "A") {
							return reject(
								AsrsUploadSingleColumnRequired(
									options?.byLpn ?? false
								)
							);
						}
					} else if (
						startCol !== "A" ||
						(endCol !== "A" && endCol !== "B")
					) {
						return reject(
							"Invalid file format. File must only use columns A and B (no more than 2 columns)."
						);
					}
				}

				resolve(jsonData as Record<string, string>[]);
			} catch (error) {
				reject(
					error instanceof Error
						? error.message
						: "Error processing file"
				);
			}
		};

		reader.onerror = (e) => {
			reject(
				"Error reading file: " +
					(e.target?.error?.message || "Unknown error")
			);
		};

		reader.readAsArrayBuffer(file);
	});
}
